import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import { fetchMovieDetail, fetchMovieBreakdown, updateManualScreeningCount, triggerScreeningRescan, fetchEvents } from '../api/client';
import api from '../api/client';
import ChartCard from '../components/ChartCard';
import DataTable from '../components/DataTable';
import RankingTable, { WoWCell } from '../components/RankingTable';
import StatCard from '../components/StatCard';
import OccBar from '../components/OccBar';
import CategoryYAxisTick from '../components/CategoryYAxisTick';
import DateRangePicker, { currentIsraeliWeek } from '../components/DateRangePicker';
import Poster from '../components/Poster';
import { useScreeningModals, LANGUAGE_LABELS } from '../components/ScreeningModals';
import { NEU_BG, NEU_RAISED, NEU_PRESSED, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_TERTIARY, TEXT_FAINT, BORDER_SUBTLE, ACCENT, ACCENT_BG } from '../theme';
import { useAuth } from '../context/AuthContext';
import WednesdayChip from '../components/WednesdayChip';
import { MoreButton } from '../components/VenueCharts';
import AnchoredMenu from '../components/AnchoredMenu';
import MovieActionPanel from '../components/MovieActionPanel';
import { ageText } from '../components/ageLabel';

const formatNumber = (val) => (val || 0).toLocaleString();

// ── Screenings on a phone ───────────────────────────────────────────────
// The screenings table has up to 13 columns and scrolled sideways on a phone.
// There it is one row per screening, grouped by day, as on the branch page.
const PHONE_PAGE = 20;
const phoneDayLabel = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('he-IL', { weekday: 'long' })} ${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const phoneBadge = { padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, border: 'none' };

function PhoneScreenings({ rows, newestFirst, actions = [], onSnapshot, onNotScanned }) {
  const { hasPermission } = useAuth();
  const [shown, setShown] = useState(PHONE_PAGE);
  // Manual update / rescan, for whoever may use them, show under a row once it
  // is tapped — on every row at once they turned the list into a column of buttons.
  const [openId, setOpenId] = useState(null);
  const now = new Date();
  const sorted = [...rows].sort((a, b) => (newestFirst ? -1 : 1) * String(a.showtime).localeCompare(String(b.showtime)));
  const visible = sorted.slice(0, shown);
  return (
    <div>
      {visible.map((r, i) => {
        const d = new Date(r.showtime);
        const day = String(r.showtime).slice(0, 10);
        const newDay = i === 0 || String(visible[i - 1].showtime).slice(0, 10) !== day;
        const past = d < now;
        const version = [r.format, r.language ? (LANGUAGE_LABELS[r.language] || r.language) : null]
          .filter(v => v && v !== 'מקור / לא צוין').join(' · ');
        let status = null;
        if (r.status === 'cancelled') {
          status = <span style={{ ...phoneBadge, background: 'var(--surf-alt)', color: 'var(--faint)' }}>מבוטלת</span>;
        } else if (past && r.final_scraped_at && hasPermission('movie_detail.snapshot_popup')) {
          status = <button onClick={(e) => { e.stopPropagation(); onSnapshot(r); }} style={{ ...phoneBadge, cursor: 'pointer', background: 'var(--ok-bg)', color: 'var(--ok)' }}>נסגר</button>;
        } else if (past && !r.final_scraped_at && r.status === 'closed' && hasPermission('movie_detail.unscan_popup')) {
          status = <button onClick={(e) => { e.stopPropagation(); onNotScanned(r); }} style={{ ...phoneBadge, cursor: 'pointer', background: 'var(--danger-bg)', color: 'var(--danger)' }}>לא נסרק</button>;
        }
        return (
          <React.Fragment key={r.id}>
            {newDay && (
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', padding: '12px 2px 4px' }}>
                {phoneDayLabel(r.showtime)}
              </div>
            )}
            <div onClick={actions.length ? () => setOpenId(id => (id === r.id ? null : r.id)) : undefined}
                 style={{ borderTop: '1px solid var(--brd)', padding: '10px 2px', cursor: actions.length ? 'pointer' : 'default' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 46, flexShrink: 0, textAlign: 'center', fontSize: 15, fontWeight: 700,
                              color: 'var(--txt)', fontVariantNumeric: 'tabular-nums' }}>
                  {d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--txt)', overflow: 'hidden',
                                textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.cinema}</div>
                  <div style={{ fontSize: 11, color: 'var(--faint)', overflow: 'hidden', textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap' }}>{[r.city, r.hall, version].filter(Boolean).join(' · ')}</div>
                </div>
                <div style={{ flexShrink: 0, textAlign: 'left' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {formatNumber(r.tickets_sold)}
                    {r.total_seats ? <span style={{ color: 'var(--faint)', fontWeight: 400 }}>/{formatNumber(r.total_seats)}</span> : null}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                    {status}
                    {r.total_seats ? <span style={{ fontSize: 11, color: 'var(--muted)' }}>{Math.round(r.occupancy || 0)}%</span> : null}
                  </div>
                </div>
              </div>
              {actions.length > 0 && openId === r.id && (
                <div onClick={(e) => e.stopPropagation()}
                     style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 8 }}>
                  {actions.map((fn, k) => <React.Fragment key={k}>{fn(r)}</React.Fragment>)}
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
      {shown < sorted.length && <MoreButton onClick={() => setShown(n => n + PHONE_PAGE)}>הצג עוד</MoreButton>}
    </div>
  );
}

function MovieDetail({ movieId, onBack, onShowdown, refreshNonce }) {
  const { hasPermission, user } = useAuth();
  const [data, setData] = useState(null);
  // The admin's action menu — the same one as on the movies list (§12.11).
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef(null);
  const menuPanelRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [manualCounts, setManualCounts] = useState({});
  const [savingIds, setSavingIds] = useState({});
  const [rescanningIds, setRescanningIds] = useState({});
  const [editOpenIds, setEditOpenIds] = useState({});
  const [rescanModal, setRescanModal] = useState(null);
  const { openSnapshot: openSnapshotModal, openNotScanned: setNotScannedModal, modals: screeningModals } = useScreeningModals();
  const [dateRange, setDateRange] = useState(() => currentIsraeliWeek());
  const [tab, setTab] = useState('screenings');
  const [width, setWidth] = useState(window.innerWidth);
  const [comps, setComps] = useState(null);
  const [compsLoading, setCompsLoading] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [scanAllState, setScanAllState] = useState({ running: false, done: 0, total: 0, errors: 0 });
  const [eventsByDate, setEventsByDate] = useState({});

  useEffect(() => {
    const fn = () => setWidth(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  // Special cinema events — date (YYYY-MM-DD) → event name, for tagging rows/charts
  useEffect(() => {
    fetchEvents().then(d => {
      const map = {};
      (d?.events || []).forEach(ev => {
        const cur = new Date(ev.start_date);
        const end = new Date(ev.end_date);
        while (cur <= end) {
          map[cur.toISOString().slice(0, 10)] = ev.name;
          cur.setDate(cur.getDate() + 1);
        }
      });
      setEventsByDate(map);
    }).catch(() => setEventsByDate({}));
  }, []);

  const mob = width < 768;

  const loadComps = useCallback(() => {
    if (comps !== null || compsLoading) return;
    setCompsLoading(true);
    api.get(`/analytics/movie-comps/${movieId}?n=4`)
      .then(r => setComps(r.data))
      .catch(() => setComps({ error: true }))
      .finally(() => setCompsLoading(false));
  }, [movieId, comps, compsLoading]);

  useEffect(() => {
    if (movieId) {
      setLoading(true);
      fetchMovieDetail(movieId, dateRange.start, dateRange.end).then(d => {
        setData(d);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [movieId, refreshNonce, dateRange]);

  // The menu is portalled, so an outside click has to test the panel too (§12.4).
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handler = (e) => {
      const inBtn = menuBtnRef.current && menuBtnRef.current.contains(e.target);
      const inPanel = menuPanelRef.current && menuPanelRef.current.contains(e.target);
      if (!inBtn && !inPanel) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const reloadMovie = () => fetchMovieDetail(movieId, dateRange.start, dateRange.end).then(d => d && d.movie && setData(d));

  // Lifetime breakdown (BOM-style summary + daily/weekend) — not date-filtered
  useEffect(() => {
    if (!movieId) return;
    setBreakdown(null);
    fetchMovieBreakdown(movieId).then(b => setBreakdown(b && !b.error ? b : null)).catch(() => setBreakdown(null));
  }, [movieId, refreshNonce]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60, color: TEXT_TERTIARY }}>טוען פרטי סרט...</div>;
  }

  if (!data || !data.movie) {
    return <div style={{ textAlign: 'center', padding: 60, color: 'var(--danger)' }}>סרט לא נמצא</div>;
  }

  const { movie, by_cinema, by_date, screenings } = data;
  const variants = data.variants || [];
  const byLanguage = data.by_language || [];
  const overview = data.overview || {};

  // BOM-style daily / weekend rows (prevTickets precomputed in chronological order for WoW)
  const dailyRows = (breakdown?.by_date || []).map((d, i, arr) => ({
    ...d, movie_id: d.date, prevTickets: i > 0 ? arr[i - 1].tickets : 0,
  }));
  const weekendRows = (breakdown?.by_weekend || []).map((w, i, arr) => ({
    ...w, movie_id: w.start, prevTickets: i > 0 ? arr[i - 1].tickets : 0,
  }));

  const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };



  const saveManualCount = async (screeningId) => {
    const raw = manualCounts[screeningId];
    if (raw == null || raw === '') return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    setSavingIds((prev) => ({ ...prev, [screeningId]: true }));
    try {
      await updateManualScreeningCount(screeningId, Math.floor(parsed));
      const refreshed = await fetchMovieDetail(movieId, dateRange.start, dateRange.end);
      setData(refreshed);
    } finally {
      setSavingIds((prev) => ({ ...prev, [screeningId]: false }));
    }
  };

  const runSingleRescan = async (screeningId) => {
    setRescanningIds((prev) => ({ ...prev, [screeningId]: true }));
    try {
      const result = await triggerScreeningRescan(screeningId);
      setRescanModal(result);
      if (!result.error) {
        const refreshed = await fetchMovieDetail(movieId, dateRange.start, dateRange.end);
        setData(refreshed);
      }
    } catch (e) {
      setRescanModal({ error: e?.message || 'שגיאה בסריקה' });
    } finally {
      setRescanningIds((prev) => ({ ...prev, [screeningId]: false }));
    }
  };





  const totalTickets = by_cinema.reduce((s, c) => s + c.tickets_sold, 0);
  const totalScreenings = by_cinema.reduce((s, c) => s + c.screenings, 0);
  const allTimeTickets = movie.all_time_tickets || 0;
  const allTimeClosedTickets = movie.all_time_closed_tickets || 0;

  const cinemaColumns = [
    { header: 'סניף', key: 'cinema' },
    { header: 'עיר', key: 'city' },
    { header: 'הקרנות', render: r => formatNumber(r.screenings), align: 'center' },
    {
      header: 'כרטיסים',
      render: r => <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{formatNumber(r.tickets_sold)}</span>,
      align: 'center',
    },
  ];

  // Screening table columns (defined once, used in all 3 buckets)
  const now = new Date();
  const cutoff3Days = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
  const pastScreenings = (screenings || []).filter(s => s.showtime && new Date(s.showtime) < now);
  const upcomingScreenings = (screenings || []).filter(s => s.showtime && new Date(s.showtime) >= now && new Date(s.showtime) <= cutoff3Days);
  const futureScreenings = (screenings || []).filter(s => s.showtime && new Date(s.showtime) > cutoff3Days);

  const runScanAll = async () => {
    const activeIds = [...upcomingScreenings, ...futureScreenings].map(s => s.id);
    if (!activeIds.length) return;
    setScanAllState({ running: true, done: 0, total: activeIds.length, errors: 0 });
    let errors = 0;
    for (let i = 0; i < activeIds.length; i++) {
      try {
        await triggerScreeningRescan(activeIds[i]);
      } catch {
        errors++;
      }
      setScanAllState({ running: true, done: i + 1, total: activeIds.length, errors });
    }
    setScanAllState({ running: false, done: activeIds.length, total: activeIds.length, errors });
    const refreshed = await fetchMovieDetail(movieId, dateRange.start, dateRange.end);
    setData(refreshed);
  };

  const screeningColumns = [
    {
      header: 'סטטוס',
      render: r => {
        const showtime = r.showtime ? new Date(r.showtime) : null;
        const isPast = showtime && showtime < now;
        const isClosed = r.status === 'closed';
        const hasFinalScrape = !!r.final_scraped_at;
        if (!isPast) {
          return <span style={{ color: 'var(--ok)', fontSize: 12, fontWeight: 500 }}>פעיל</span>;
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{
              display: 'inline-block', padding: '2px 6px', borderRadius: 4,
              fontSize: 10, fontWeight: 600, background: 'rgba(100,100,100,0.2)', color: TEXT_SECONDARY,
            }}>
              עבר
            </span>
            {isClosed && hasFinalScrape && hasPermission('movie_detail.snapshot_popup') && (
              <button
                onClick={() => openSnapshotModal(r)}
                title={`סריקה סופית: ${new Date(r.final_scraped_at).toLocaleString('he-IL')} — לחץ לפרטים`}
                style={{
                  display: 'inline-block', padding: '2px 6px', borderRadius: 4,
                  fontSize: 10, fontWeight: 600, cursor: 'pointer', border: 'none',
                  background: 'var(--ok-bg)', color: 'var(--ok)',
                }}
              >נסגר</button>
            )}
            {isClosed && !hasFinalScrape && hasPermission('movie_detail.unscan_popup') && (
              <button
                onClick={() => setNotScannedModal(r)}
                title="לא נסרק סופית — לחץ לפרטים"
                style={{
                  display: 'inline-block', padding: '2px 6px', borderRadius: 4,
                  fontSize: 10, fontWeight: 600, cursor: 'pointer', border: 'none',
                  background: 'rgba(239,68,68,0.2)', color: 'var(--danger)',
                }}
              >לא נסרק</button>
            )}
            {r.status === 'cancelled' && (
              <span
                title="CC הסירה הקרנה זו מה-API שלהם"
                style={{
                  display: 'inline-block', padding: '2px 6px', borderRadius: 4,
                  fontSize: 10, fontWeight: 600,
                  background: 'rgba(107,114,128,0.2)', color: 'var(--faint)',
                }}
              >מבוטלת</span>
            )}
          </div>
        );
      },
      align: 'center',
    },
    {
      header: 'תאריך',
      render: r => r.showtime
        ? new Date(r.showtime).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' })
        : '-',
      align: 'center',
    },
    {
      header: 'שעה',
      render: r => r.showtime
        ? new Date(r.showtime).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
        : '-',
      align: 'center',
    },
    { header: 'סניף', key: 'cinema' },
    { header: 'עיר', key: 'city' },
    { header: 'אולם', key: 'hall', align: 'center' },
    {
      header: 'פורמט',
      render: r => {
        const parts = [];
        if (r.format) parts.push(r.format);
        const langLabel = r.language ? (LANGUAGE_LABELS[r.language] || r.language) : null;
        if (langLabel && langLabel !== 'מקור / לא צוין') parts.push(langLabel);
        return parts.length
          ? <span style={{ fontSize: 11, fontWeight: 500 }}>{parts.join(' | ')}</span>
          : <span style={{ color: TEXT_TERTIARY, fontSize: 11 }}>רגיל</span>;
      },
      align: 'center',
    },
    {
      header: 'סה"כ מקומות',
      render: r => r.total_seats > 0
        ? formatNumber(r.total_seats)
        : <span style={{ color: TEXT_TERTIARY }}>—</span>,
      align: 'center',
    },
    {
      header: 'נמכרו',
      render: r => r.total_seats > 0
        ? (
          <span style={{ color: r.tickets_sold > 0 ? 'var(--warn)' : TEXT_TERTIARY, fontWeight: 600 }}>
            {formatNumber(r.tickets_sold)}
            {r.blocked_seats_excluded > 0 && (
              <span
                title={`${r.blocked_seats_excluded} מושבים חסומים סוננו (לפני סינון: ${formatNumber(r.tickets_sold_raw)})`}
                style={{ color: 'var(--danger)', fontSize: 11, marginRight: 4, cursor: 'help' }}
              >*</span>
            )}
          </span>
        )
        : <span style={{ color: TEXT_TERTIARY }}>—</span>,
      align: 'center',
    },
    {
      header: 'תפוסה',
      render: r => {
        if (!r.total_seats) return <span style={{ color: TEXT_TERTIARY, fontSize: 12 }}>—</span>;
        const occ = r.occupancy || 0;
        const color = occ > 75 ? 'var(--danger)' : occ > 40 ? 'var(--warn)' : 'var(--ok)';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
            <div style={{ width: 50, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(occ, 100)}%`, height: '100%', background: color, borderRadius: 3 }} />
            </div>
            <span style={{ color, fontSize: 12, fontWeight: 600 }}>{Math.round(occ)}%</span>
          </div>
        );
      },
      align: 'center',
    },
    {
      header: 'עדכון ידני',
      show: hasPermission('movie_detail.manual_update'),
      render: r => {
        const isOpen = !!editOpenIds[r.id];
        if (!isOpen) {
          return (
            <button
              onClick={() => setEditOpenIds(p => ({ ...p, [r.id]: true }))}
              title="עדכן ידנית"
              style={{
                border: `1px solid ${BORDER_SUBTLE}`, borderRadius: 6,
                padding: '3px 8px', fontSize: 11, background: 'transparent',
                color: TEXT_SECONDARY, cursor: 'pointer',
              }}
            >✎</button>
          );
        }
        return (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input
              type="number"
              min="0"
              autoFocus
              value={manualCounts[r.id] ?? ''}
              onChange={(e) => setManualCounts((prev) => ({ ...prev, [r.id]: e.target.value }))}
              placeholder={String(r.tickets_sold || 0)}
              style={{
                width: 64, padding: '3px 5px', borderRadius: 6,
                border: `1px solid ${BORDER_SUBTLE}`, fontSize: 12,
              }}
            />
            <button
              onClick={async () => { await saveManualCount(r.id); setEditOpenIds(p => ({ ...p, [r.id]: false })); }}
              disabled={!!savingIds[r.id]}
              style={{
                border: 'none', borderRadius: 6, padding: '3px 7px', fontSize: 11, fontWeight: 600,
                cursor: savingIds[r.id] ? 'default' : 'pointer',
                background: savingIds[r.id] ? 'var(--faint)' : 'var(--accent)', color: '#fff',
              }}
            >{savingIds[r.id] ? '...' : '✓'}</button>
            <button
              onClick={() => setEditOpenIds(p => ({ ...p, [r.id]: false }))}
              style={{
                border: 'none', borderRadius: 6, padding: '3px 6px', fontSize: 11,
                cursor: 'pointer', background: 'transparent', color: TEXT_TERTIARY,
              }}
            >✕</button>
          </div>
        );
      },
      align: 'center',
    },
    {
      header: 'סריקה מיידית',
      show: hasPermission('movie_detail.scan_now'),
      render: r => (
        <button
          onClick={() => runSingleRescan(r.id)}
          disabled={!!rescanningIds[r.id]}
          style={{
            border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 11, fontWeight: 600,
            cursor: rescanningIds[r.id] ? 'default' : 'pointer',
            background: rescanningIds[r.id] ? 'var(--faint)' : 'var(--ok)', color: '#fff',
          }}
        >
          {rescanningIds[r.id] ? 'סורק...' : 'סרוק עכשיו'}
        </button>
      ),
      align: 'center',
    },
    {
      header: 'סריקה אחרונה',
      show: hasPermission('movie_detail.last_scan'),
      render: r => r.scraped_at
        ? new Date(r.scraped_at).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
        : <span style={{ color: TEXT_TERTIARY, fontSize: 12 }}>—</span>,
      align: 'center',
    },
  ].filter(c => c.show !== false);

  const phoneProps = {
    actions: screeningColumns.filter(c => c.header === 'עדכון ידני' || c.header === 'סריקה מיידית').map(c => c.render),
    onSnapshot: openSnapshotModal,
    onNotScanned: setNotScannedModal,
  };

  const collapsibleStyle = {
    marginTop: 12, borderRadius: 8, overflow: 'hidden',
    border: `1px solid ${BORDER_SUBTLE}`,
  };
  const summaryStyle = {
    padding: '10px 16px', cursor: 'pointer', fontSize: 15, fontWeight: 600,
    background: 'var(--surf-alt)', userSelect: 'none', listStyle: 'none',
    display: 'flex', alignItems: 'center', gap: 8,
  };

  return (
    <div>
      {/* Rescan result modal */}
      {rescanModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }} onClick={() => setRescanModal(null)}>
          <div style={{
            background: NEU_BG, borderRadius: 14, padding: '28px 32px', minWidth: 320, maxWidth: 420,
            boxShadow: '0 8px 40px rgba(0,0,0,0.4)', position: 'relative',
          }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setRescanModal(null)} style={{
              position: 'absolute', top: 12, left: 16, background: 'none', border: 'none',
              fontSize: 20, cursor: 'pointer', color: TEXT_TERTIARY, lineHeight: 1,
            }}>✕</button>
            {rescanModal.error ? (
              <>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--danger)', marginBottom: 12 }}>סריקה נכשלה</div>
                <div style={{ color: TEXT_SECONDARY, fontSize: 13 }}>{rescanModal.error}</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>תוצאות סריקה</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14, color: TEXT_SECONDARY }}>
                  <div><span style={{ color: TEXT_TERTIARY, fontSize: 12 }}>סרט:</span> {rescanModal.movie}</div>
                  <div><span style={{ color: TEXT_TERTIARY, fontSize: 12 }}>סניף:</span> {rescanModal.cinema}{rescanModal.hall ? ` | אולם ${rescanModal.hall}` : ''}</div>
                  {rescanModal.showtime && (
                    <div><span style={{ color: TEXT_TERTIARY, fontSize: 12 }}>שעה:</span>{' '}
                      {new Date(rescanModal.showtime).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                  <div style={{ marginTop: 8, display: 'flex', gap: 24 }}>
                    <div>
                      <div style={{ color: TEXT_TERTIARY, fontSize: 11 }}>סה״כ מושבים</div>
                      <div style={{ fontSize: 22, fontWeight: 700 }}>{(rescanModal.total_seats || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={{ color: TEXT_TERTIARY, fontSize: 11 }}>נמכרו</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--warn)' }}>{(rescanModal.tickets_sold || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={{ color: TEXT_TERTIARY, fontSize: 11 }}>תפוסה</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: rescanModal.occupancy > 75 ? 'var(--danger)' : rescanModal.occupancy > 40 ? 'var(--warn)' : 'var(--ok)' }}>
                        {rescanModal.occupancy || 0}%
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {screeningModals}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer',
            fontSize: 14, fontFamily: 'Heebo, sans-serif', padding: 0,
          }}
        >
          ← חזרה לרשימת הסרטים
        </button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {user?.is_admin && (
          <button
            ref={menuBtnRef}
            data-movie-menu
            onClick={() => setMenuOpen(o => !o)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: menuOpen ? 'var(--accent)' : 'var(--surf-alt)', border: `1px solid ${menuOpen ? 'var(--accent)' : 'var(--brd)'}`,
              color: menuOpen ? '#fff' : 'var(--txt)',
              cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'Heebo, sans-serif',
              padding: '7px 14px', borderRadius: 9,
            }}
          >✎ עריכה</button>
        )}
        {menuOpen && (
          <AnchoredMenu anchorRef={menuBtnRef} panelRef={menuPanelRef}>
            <MovieActionPanel movie={movie} onClose={() => setMenuOpen(false)} onRefresh={reloadMovie} />
          </AnchoredMenu>
        )}
        {onShowdown && (
          <button
            onClick={() => onShowdown(movie.id)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'var(--surf-alt)', border: '1px solid var(--brd)', color: 'var(--txt)',
              cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'Heebo, sans-serif',
              padding: '7px 14px', borderRadius: 9,
            }}
          >
            ⚔️ השוואה לסרט אחר
          </button>
        )}
        </div>
      </div>

      {/* Movie Header — info + integrated weekly performance */}
      <div style={{
        background: NEU_BG, borderRadius: 16, padding: mob ? '18px 18px' : '24px 28px',
        boxShadow: NEU_RAISED, marginBottom: 16, display: 'flex', gap: mob ? 14 : 22,
        alignItems: 'flex-start',
      }}>
        <Poster
          posterUrl={movie.poster_url}
          genre={movie.genre}
          letter={(movie.title_he || movie.title || '?')[0]}
          size={mob ? 'md' : 'xl'}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: mob ? 18 : 24, fontWeight: 800, letterSpacing: -0.5, marginBottom: 3 }}>
            {movie.title_he || movie.title}
          </h2>
          {movie.title_en && (
            <div style={{ color: TEXT_SECONDARY, fontSize: 13, marginBottom: 10 }}>{movie.title_en}</div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {movie.genre && (
              <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'var(--surf-alt)', color: TEXT_SECONDARY, border: `1px solid ${BORDER_SUBTLE}` }}>{movie.genre}</span>
            )}
            {!mob && movie.duration_minutes > 0 && (
              <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'var(--surf-alt)', color: TEXT_SECONDARY, border: `1px solid ${BORDER_SUBTLE}` }}>{movie.duration_minutes} ד׳</span>
            )}
            {!mob && movie.director && (
              <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'var(--surf-alt)', color: TEXT_SECONDARY, border: `1px solid ${BORDER_SUBTLE}` }}>{movie.director}</span>
            )}
            {movie.age_label && (
              <span data-age-chip style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', border: '1px solid rgba(239,68,68,0.18)' }}>
                {ageText(movie.age_label)}
              </span>
            )}
            {movie.distributor && (
              <span data-distributor-chip style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'var(--accent-bg)', color: 'var(--accent)' }}>
                {movie.distributor}
              </span>
            )}
            {movie.tmdb_rating > 0 && (
              <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'rgba(234,179,8,0.1)', color: 'var(--warn)', border: '1px solid rgba(234,179,8,0.18)' }}>
                ★ {movie.tmdb_rating.toFixed(1)}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, marginBottom: 8, color: TEXT_SECONDARY }}>
            {movie.release_date_il && (
              <span title="תאריך יציאה בישראל (TMDB)">🇮🇱 יציאה בישראל: <strong>{movie.release_date_il}</strong></span>
            )}
            {!movie.release_date_il && movie.release_date && (
              <span title="תאריך יציאה כללי">📅 תאריך יציאה: <strong>{movie.release_date}</strong></span>
            )}
            {movie.first_screening_date && (
              <span title="תאריך ההקרנה הראשונה שנמצאת במאגר שלנו">🎬 הקרנה ראשונה במאגר: <strong>{formatDate(movie.first_screening_date)}</strong></span>
            )}
            {movie.first_ticket_date && (
              <span title="תאריך שנתפס הכיסא הראשון">🎟 מכירה ראשונה שנתפסה: <strong>{formatDate(movie.first_ticket_date)}</strong></span>
            )}
          </div>
          {movie.overview_he && (
            <div style={{ color: TEXT_SECONDARY, fontSize: 13, lineHeight: 1.6, marginBottom: 12, maxWidth: 600 }}>
              {movie.overview_he}
            </div>
          )}

          {/* All-time summary stats */}
          {hasPermission('movie_detail.stats') && (
            <>
              <div style={{
                display: mob ? 'grid' : 'flex',
                gridTemplateColumns: mob ? '1fr 1fr' : undefined,
                gap: mob ? 16 : 32,
                marginBottom: overview.this_week_tickets != null ? 12 : 0,
              }}>
                {[
                  ['כרטיסים בטווח שנבחר', formatNumber(totalTickets), ACCENT],
                  ['הקרנות', formatNumber(totalScreenings), 'var(--warn)'],
                  ['סניפים', String(by_cinema.length), TEXT_PRIMARY],
                ].map(([label, val, color]) => (
                  <div key={label}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: TEXT_FAINT, marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color, letterSpacing: -1 }}>{val}</div>
                  </div>
                ))}
                {!mob && <div style={{ width: 1, background: BORDER_SUBTLE, alignSelf: 'stretch', margin: '0 8px' }} />}
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: TEXT_FAINT, marginBottom: 4 }}>כרטיסים בסך הכל</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: ACCENT, letterSpacing: -1 }}>{formatNumber(allTimeTickets)}</div>
                  <div style={{ fontSize: 10, color: TEXT_FAINT, marginTop: 2 }}>הקרנות שנסגרו: {formatNumber(allTimeClosedTickets)}</div>
                </div>
              </div>
              {/* Weekly performance — integrated naturally into header */}
              {overview.this_week_tickets != null && (
                <div style={{
                  display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
                  paddingTop: 12, borderTop: `1px solid ${BORDER_SUBTLE}`, fontSize: 13,
                }}>
                  <span style={{ color: TEXT_TERTIARY, fontSize: 12 }}>השבוע:</span>
                  <span style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 14 }}>
                    {formatNumber(overview.this_week_tickets)} כרטיסים
                  </span>
                  {overview.week_trend_pct != null && (
                    <span style={{ fontWeight: 600, color: overview.week_trend_pct > 0 ? 'var(--ok)' : 'var(--danger)' }}>
                      {overview.week_trend_pct > 0 ? '▲' : '▼'} {Math.abs(Math.round(overview.week_trend_pct))}%
                    </span>
                  )}
                  {overview.avg_occupancy_pct != null && (
                    <span style={{ color: TEXT_SECONDARY }}>תפוסה {Math.round(overview.avg_occupancy_pct)}%</span>
                  )}
                  {overview.best_branch && (
                    <span style={{ color: TEXT_SECONDARY }}>
                      סניף מוביל: <strong>{overview.best_branch}</strong>
                    </span>
                  )}
                  {overview.competition?.length > 0 && (
                    <span style={{ color: TEXT_TERTIARY, fontSize: 12 }}>
                      תחרות: {overview.competition.map(c => c.title).join(', ')}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* BOM-style lifetime summary */}
      {hasPermission('movie_detail.stats') && breakdown && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_SECONDARY, marginBottom: 10 }}>סיכום</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <StatCard title="כרטיסים — סה״כ" value={formatNumber(breakdown.total_tickets)} main />
            <StatCard title="תפוסה ממוצעת" value={breakdown.avg_occupancy ? `${Math.round(breakdown.avg_occupancy)}%` : '—'} color={ACCENT} />
            <StatCard title="הקרנות" value={formatNumber(breakdown.total_screenings)} color="var(--warn)" />
            {breakdown.opening_weekend && (
              <StatCard title="סופ״ש פתיחה" value={formatNumber(breakdown.opening_weekend.tickets)}
                subtitle={breakdown.opening_weekend.includes_wednesday
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {breakdown.opening_weekend.label}<WednesdayChip />
                    </span>
                  : breakdown.opening_weekend.label} />
            )}
            {breakdown.peak_day && (
              <StatCard title="שיא יומי" value={formatNumber(breakdown.peak_day.tickets)} subtitle={formatDate(breakdown.peak_day.date)} />
            )}
            <StatCard title="שבועות בהקרנה" value={breakdown.weeks_in_release || '—'} />
            <StatCard title="סניפים · אולמות" value={`${breakdown.cinemas_count} · ${breakdown.halls_count}`} />
          </div>
        </div>
      )}

      {/* Date range picker + range summary */}
      <DateRangePicker
        start={dateRange.start}
        end={dateRange.end}
        onChange={setDateRange}
        label="ניתוח לפי טווח תאריכים"
      />
      {data.range_summary && (
        <div style={{
          display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 16,
          padding: '12px 20px', background: NEU_BG, borderRadius: 12, boxShadow: NEU_RAISED,
        }}>
          {[
            ['כרטיסים בטווח', formatNumber(data.range_summary.total_tickets), ACCENT],
            ['הקרנות בטווח', formatNumber(data.range_summary.total_screenings), 'var(--warn)'],
          ].map(([label, val, color]) => (
            <div key={label}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: TEXT_FAINT, marginBottom: 3 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color, letterSpacing: -1 }}>{val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tab bar */}
      {(() => {
        const availableTabs = [
          hasPermission('movie_detail.screenings_tab') && { k: 'screenings', l: 'הקרנות' },
          hasPermission('movie_detail.charts_tab') && { k: 'daily', l: 'יומי' },
          hasPermission('movie_detail.charts_tab') && { k: 'weekend', l: 'סופ״ש' },
          hasPermission('movie_detail.charts_tab') && { k: 'charts', l: 'גרפים' },
          hasPermission('movie_detail.cinemas_tab') && { k: 'cinemas', l: 'סניפים' },
          hasPermission('movie_detail.comps_tab') && { k: 'comps', l: 'קומפים' },
        ].filter(Boolean);
        if (availableTabs.length === 0) return null;
        const activeTab = availableTabs.find(t => t.k === tab) ? tab : availableTabs[0].k;
        if (activeTab !== tab) setTimeout(() => setTab(activeTab), 0);
        return (
          <div style={{ display: 'flex', marginBottom: 16, borderBottom: `1px solid ${BORDER_SUBTLE}` }}>
            {availableTabs.map(t => (
              <button key={t.k} onClick={() => { setTab(t.k); if (t.k === 'comps') loadComps(); }} style={{
                // On a phone the tabs share the width; at 20px padding the last one fell off the screen.
                padding: mob ? '9px 2px' : '9px 20px', flex: mob ? 1 : undefined, minWidth: 0, whiteSpace: 'nowrap',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: mob ? 12.5 : 13, fontWeight: tab === t.k ? 700 : 400,
                color: tab === t.k ? ACCENT : TEXT_SECONDARY,
                fontFamily: 'Heebo, sans-serif', transition: 'all .15s',
                borderBottom: tab === t.k ? `2px solid ${ACCENT}` : '2px solid transparent',
                marginBottom: -1,
              }}>{t.l}</button>
            ))}
          </div>
        );
      })()}

      {/* ── Tab: הקרנות ── */}
      {hasPermission('movie_detail.screenings_tab') && tab === 'screenings' && (
      <div style={{ animation: 'fadeIn .18s ease' }}>
      {screenings && screenings.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          {hasPermission('movie_detail.scan_now') && (upcomingScreenings.length + futureScreenings.length) > 0 && (
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={runScanAll}
                disabled={scanAllState.running}
                style={{
                  background: scanAllState.running ? '#166534' : '#22c55e',
                  color: '#fff', border: 'none', borderRadius: 8,
                  padding: '8px 20px', fontSize: 14, fontWeight: 700,
                  cursor: scanAllState.running ? 'not-allowed' : 'pointer',
                  opacity: scanAllState.running ? 0.8 : 1,
                }}
              >
                {scanAllState.running
                  ? `סורק... ${scanAllState.done}/${scanAllState.total}`
                  : 'סרוק הכל'}
              </button>
              {!scanAllState.running && scanAllState.total > 0 && (
                <span style={{ fontSize: 12, color: scanAllState.errors ? 'var(--danger)' : 'var(--ok)' }}>
                  {scanAllState.errors
                    ? `הושלם עם ${scanAllState.errors} שגיאות`
                    : `נסרקו ${scanAllState.total} הקרנות`}
                </span>
              )}
            </div>
          )}
          <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_SECONDARY, marginBottom: 10 }}>
            3 הימים הקרובים{upcomingScreenings.length > 0 ? ` (${upcomingScreenings.length})` : ''}
          </div>

          {upcomingScreenings.length > 0 ? (
            mob ? <PhoneScreenings rows={upcomingScreenings} {...phoneProps} />
                : <div style={{ overflowX: 'auto' }}><DataTable columns={screeningColumns} data={upcomingScreenings} /></div>
          ) : (
            <div style={{ color: TEXT_TERTIARY, fontSize: 13, marginBottom: 8 }}>
              אין הקרנות ב-3 הימים הקרובים
            </div>
          )}

          {futureScreenings.length > 0 && (
            <details style={collapsibleStyle}>
              <summary style={summaryStyle}>
                <span style={{ color: 'var(--accent)' }}>▶</span>
                הקרנות עתידיות ({futureScreenings.length})
              </summary>
              <div style={{ padding: '0 0 8px' }}>
                {mob ? <PhoneScreenings rows={futureScreenings} {...phoneProps} />
                     : <div style={{ overflowX: 'auto' }}><DataTable columns={screeningColumns} data={futureScreenings} /></div>}
              </div>
            </details>
          )}

          {pastScreenings.length > 0 && (
            <details style={collapsibleStyle}>
              <summary style={summaryStyle}>
                <span style={{ color: TEXT_TERTIARY }}>▶</span>
                ארכיון הקרנות ({pastScreenings.length})
              </summary>
              <div style={{ padding: '0 0 8px' }}>
                {mob ? <PhoneScreenings rows={pastScreenings} newestFirst {...phoneProps} />
                     : <DataTable columns={screeningColumns} data={pastScreenings} />}
              </div>
            </details>
          )}
        </div>
      )}
      </div>
      )} {/* end tab: screenings */}

      {/* ── Tab: גרפים ── */}
      {hasPermission('movie_detail.charts_tab') && tab === 'charts' && (
        <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 16, animation: 'fadeIn .18s ease' }}>
          <ChartCard title="כרטיסים יומיים">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={by_date} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="g-mv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={ACCENT} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={BORDER_SUBTLE} vertical={false} />
                <XAxis dataKey="date" tick={{ fill: 'var(--faint)', fontSize: 10 }} tickLine={false} axisLine={false}
                       tickFormatter={d => d ? d.slice(5) : ''} />
                <YAxis tick={{ fill: 'var(--faint)', fontSize: 10 }} tickLine={false} axisLine={false}
                       tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                <Tooltip
                  contentStyle={{ background: 'var(--surf)', border: 'none', borderRadius: 12, boxShadow: 'var(--shadow)', color: 'var(--txt)', fontFamily: 'Heebo,sans-serif', fontSize: 13 }}
                  formatter={(v) => [formatNumber(v), 'כרטיסים']}
                />
                <Area type="monotone" dataKey="tickets_sold" stroke={ACCENT} fill="url(#g-mv)"
                      strokeWidth={2} dot={false} activeDot={{ r: 5, fill: ACCENT, strokeWidth: 0 }} name="כרטיסים" />
                <ReferenceLine x={new Date().toISOString().slice(0, 10)} stroke={ACCENT} strokeDasharray="4 2"
                  label={{ value: 'היום', position: 'insideTopRight', fill: ACCENT, fontSize: 11 }} />
                {Object.keys(eventsByDate)
                  .filter(d => (by_date || []).some(x => x.date === d))
                  .map(d => (
                    <ReferenceLine key={d} x={d} stroke="#f59e0b" strokeDasharray="3 3"
                      label={{ value: eventsByDate[d], position: 'insideTop', fill: '#b45309', fontSize: 10 }} />
                  ))}
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="כרטיסים לפי סניף">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={by_cinema.slice(0, 8)} layout="vertical" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={BORDER_SUBTLE} horizontal={false} />
                <XAxis type="number" tick={{ fill: 'var(--faint)', fontSize: 10 }} tickLine={false} axisLine={false}
                       tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                <YAxis type="category" dataKey="cinema" tick={{ fill: 'var(--txt)', fontSize: 11 }}
                       tickLine={false} axisLine={false} width={90} />
                <Tooltip
                  contentStyle={{ background: 'var(--surf)', border: 'none', borderRadius: 12, boxShadow: 'var(--shadow)', color: 'var(--txt)', fontFamily: 'Heebo,sans-serif', fontSize: 13 }}
                  formatter={(v) => [formatNumber(v), 'כרטיסים']}
                />
                <Bar dataKey="tickets_sold" fill={ACCENT} fillOpacity={0.8} radius={[0, 5, 5, 0]} maxBarSize={24} name="כרטיסים" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* ── Tab: סניפים ── */}
      {hasPermission('movie_detail.cinemas_tab') && tab === 'cinemas' && (
        <div style={{ animation: 'fadeIn .18s ease' }}>
          <div style={{ overflowX: 'auto' }}><DataTable columns={cinemaColumns} data={by_cinema} /></div>

          {(variants.length > 0 || byLanguage.length > 1) && (
            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 12 }}>גרסאות ושפות</div>
              {variants.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  <span style={{ padding: '6px 12px', background: NEU_BG, boxShadow: NEU_RAISED, borderRadius: 12, fontSize: 13, color: TEXT_PRIMARY }}>
                    {movie.title} <span style={{ color: TEXT_TERTIARY, marginInlineStart: 6 }}>(קנוני)</span>
                  </span>
                  {variants.map(v => (
                    <span key={v.id} style={{ padding: '6px 12px', background: 'var(--surf-alt)', borderRadius: 12, fontSize: 13, color: TEXT_SECONDARY, border: `1px solid ${BORDER_SUBTLE}` }}>{v.title}</span>
                  ))}
                </div>
              )}
              {byLanguage.length > 0 && (
                <DataTable
                  columns={[
                    { header: 'גרסה', render: r => LANGUAGE_LABELS[r.language] || r.language || 'מקור / לא צוין' },
                    { header: 'הקרנות', render: r => formatNumber(r.screenings), align: 'center' },
                    { header: 'כרטיסים', render: r => <span style={{ color: ACCENT, fontWeight: 700 }}>{formatNumber(r.tickets_sold)}</span>, align: 'center' },
                    { header: 'תפוסה', render: r => r.total_seats ? `${Math.round((r.tickets_sold || 0) * 100 / r.total_seats)}%` : '—', align: 'center' },
                  ]}
                  data={byLanguage}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: קומפים ── */}
      {tab === 'comps' && (
        <div style={{ animation: 'fadeIn .18s ease' }}>
          {compsLoading && (
            <div style={{ textAlign: 'center', padding: 48, color: TEXT_TERTIARY }}>מחשב קומפים...</div>
          )}
          {!compsLoading && (!comps || comps.error) && (
            <div style={{ textAlign: 'center', padding: 48, color: TEXT_TERTIARY }}>שגיאה בטעינת קומפים</div>
          )}
          {!compsLoading && comps && !comps.error && (() => {
            const target = comps.target;
            const compList = comps.comps || [];
            const poolSize = comps.pool_size || 0;

            // Merge trajectory data for the chart
            // Build a unified day-offset map
            const dayMap = {};
            if (target && target.trajectory) {
              target.trajectory.forEach(p => {
                if (!dayMap[p.day_offset]) dayMap[p.day_offset] = { day: p.day_offset };
                dayMap[p.day_offset]['__target'] = p.daily_tickets;
              });
            }
            compList.forEach((comp, ci) => {
              (comp.trajectory || []).forEach(p => {
                if (!dayMap[p.day_offset]) dayMap[p.day_offset] = { day: p.day_offset };
                dayMap[p.day_offset][`comp_${ci}`] = p.daily_tickets;
              });
            });
            const chartData = Object.values(dayMap).sort((a, b) => a.day - b.day);

            const COMP_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7f7f'];

            return (
              <>
                {/* Metadata row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, color: TEXT_TERTIARY }}>
                    נבדקו <strong style={{ color: TEXT_PRIMARY }}>{poolSize}</strong> סרטים היסטוריים סגורים
                  </span>
                  {compList.length === 0 && (
                    <span style={{ fontSize: 13, color: 'var(--danger)' }}>
                      — לא נמצאו קומפים עם מספיק נתונים
                    </span>
                  )}
                </div>

                {compList.length > 0 && (
                  <>
                    {/* Chart */}
                    <ChartCard title="כרטיסים יומיים — השוואה לקומפים">
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                          <CartesianGrid stroke={BORDER_SUBTLE} vertical={false} />
                          <XAxis
                            dataKey="day"
                            tick={{ fill: 'var(--faint)', fontSize: 10 }}
                            tickLine={false} axisLine={false}
                            tickFormatter={v => `יום ${v}`}
                          />
                          <YAxis
                            tick={{ fill: 'var(--faint)', fontSize: 10 }}
                            tickLine={false} axisLine={false}
                            tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
                          />
                          <Tooltip
                            contentStyle={{ background: 'var(--surf)', border: 'none', borderRadius: 12, boxShadow: 'var(--shadow)', color: 'var(--txt)', fontFamily: 'Heebo,sans-serif', fontSize: 13 }}
                            formatter={(v, name) => [formatNumber(v), name]}
                            labelFormatter={v => `יום ${v}`}
                          />
                          <Legend wrapperStyle={{ fontSize: 12, fontFamily: 'Heebo,sans-serif' }} />
                          {/* Target movie */}
                          <Line
                            type="monotone"
                            dataKey="__target"
                            name={target.title_he || target.title}
                            stroke={ACCENT}
                            strokeWidth={2.5}
                            dot={false}
                            activeDot={{ r: 5, fill: ACCENT, strokeWidth: 0 }}
                            connectNulls={false}
                          />
                          {/* Comp movies */}
                          {compList.map((comp, ci) => (
                            <Line
                              key={comp.movie_id}
                              type="monotone"
                              dataKey={`comp_${ci}`}
                              name={comp.title_he || comp.title}
                              stroke={COMP_COLORS[ci % COMP_COLORS.length]}
                              strokeWidth={1.5}
                              strokeDasharray="5 3"
                              dot={false}
                              activeDot={{ r: 4 }}
                              connectNulls={false}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </ChartCard>

                    {/* Comp cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, marginTop: 20 }}>
                      {compList.map((comp, ci) => (
                        <div key={comp.movie_id} style={{
                          background: 'var(--surf)',
                          border: `1px solid ${BORDER_SUBTLE}`,
                          borderRadius: 14,
                          padding: 14,
                          position: 'relative',
                          overflow: 'hidden',
                        }}>
                          {/* Similarity badge */}
                          <div style={{
                            position: 'absolute', top: 10, insetInlineEnd: 10,
                            background: COMP_COLORS[ci % COMP_COLORS.length] + '22',
                            border: `1px solid ${COMP_COLORS[ci % COMP_COLORS.length]}44`,
                            borderRadius: 8, padding: '2px 7px',
                            fontSize: 11, fontWeight: 700,
                            color: COMP_COLORS[ci % COMP_COLORS.length],
                          }}>
                            {Math.round((comp.similarity_score || 0) * 100)}% דמיון
                          </div>

                          {/* Poster thumbnail */}
                          {comp.tmdb_poster_url && (
                            <img
                              src={comp.tmdb_poster_url}
                              alt={comp.title}
                              style={{ width: 44, height: 66, objectFit: 'cover', borderRadius: 6, float: 'inline-start', marginInlineEnd: 10, marginBottom: 4 }}
                            />
                          )}

                          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 4, lineHeight: 1.3, paddingInlineEnd: 56 }}>
                            {comp.title_he || comp.title}
                          </div>
                          {comp.title_he && comp.title !== comp.title_he && (
                            <div style={{ fontSize: 11, color: TEXT_TERTIARY, marginBottom: 6 }}>{comp.title}</div>
                          )}

                          <div style={{ clear: 'both' }} />

                          {/* Genres */}
                          {comp.tmdb_genres && comp.tmdb_genres.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                              {[...comp.tmdb_genres].slice(0, 3).map(g => (
                                <span key={g} style={{ fontSize: 10, padding: '2px 6px', background: ACCENT_BG, borderRadius: 6, color: ACCENT, fontWeight: 600 }}>{g}</span>
                              ))}
                            </div>
                          )}

                          {/* Stats */}
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                              <div style={{ fontSize: 10, color: TEXT_FAINT, marginBottom: 1 }}>סה"כ כרטיסים</div>
                              <div style={{ fontSize: 15, fontWeight: 800, color: ACCENT }}>{formatNumber(comp.total_tickets)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: TEXT_FAINT, marginBottom: 1 }}>ימי הקרנה</div>
                              <div style={{ fontSize: 15, fontWeight: 800, color: TEXT_PRIMARY }}>{comp.duration_days}</div>
                            </div>
                            {comp.tmdb_rating > 0 && (
                              <div>
                                <div style={{ fontSize: 10, color: TEXT_FAINT, marginBottom: 1 }}>TMDB</div>
                                <div style={{ fontSize: 15, fontWeight: 800, color: TEXT_SECONDARY }}>★ {comp.tmdb_rating?.toFixed(1)}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Projection hint */}
                    {!target.is_complete && (
                      <div style={{ marginTop: 20, padding: '12px 16px', background: ACCENT_BG, borderRadius: 12, fontSize: 13, color: TEXT_SECONDARY }}>
                        💡 <strong>תחזית:</strong> אם הסרט ימשיך כמו הקומפ הדומה ביותר (
                        <strong>{compList[0]?.title_he || compList[0]?.title}</strong>
                        ), הטוטאל הצפוי: <strong style={{ color: ACCENT }}>{formatNumber(compList[0]?.total_tickets)}</strong> כרטיסים
                        על פני <strong>{compList[0]?.duration_days}</strong> ימים.
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* ── Tab: יומי (BOM daily breakdown) ── */}
      {hasPermission('movie_detail.charts_tab') && tab === 'daily' && (
        <div style={{ animation: 'fadeIn .18s ease' }}>
          {!breakdown ? (
            <div style={{ textAlign: 'center', padding: 48, color: TEXT_TERTIARY }}>טוען...</div>
          ) : dailyRows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: TEXT_TERTIARY }}>אין נתונים יומיים</div>
          ) : (
            <RankingTable
              columns={[
                { key: 'date', header: 'תאריך', align: 'right', sortValue: r => r.date,
                  render: r => (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: TEXT_PRIMARY }}>{formatDate(r.date)}</span>
                        {eventsByDate[r.date] && (
                          <span style={{
                            fontSize: 9.5, fontWeight: 800, color: '#b45309', background: 'rgba(234,179,8,0.18)',
                            padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap',
                          }}>🎬 {eventsByDate[r.date]}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: TEXT_FAINT }}>יום {r.day_number}</div>
                    </div>
                  ) },
                { key: 'tickets', header: 'כרטיסים', align: 'center',
                  render: r => <span style={{ fontWeight: 700, color: ACCENT }}>{formatNumber(r.tickets)}</span> },
                { key: 'wow', header: 'מגמה', align: 'center', sortValue: r => WoWCell.sortValue(r.tickets, r.prevTickets),
                  render: r => <WoWCell curr={r.tickets} prev={r.prevTickets} /> },
                { key: 'screenings', header: 'הקרנות', align: 'center',
                  render: r => <span style={{ color: 'var(--muted)' }}>{r.screenings || '—'}</span> },
                { key: 'occupancy', header: 'תפוסה', align: 'center',
                  render: r => <div style={{ display: 'inline-flex' }}><OccBar value={r.occupancy} /></div> },
                { key: 'cumulative', header: 'מצטבר', align: 'center',
                  render: r => <span style={{ color: TEXT_SECONDARY }}>{formatNumber(r.cumulative)}</span> },
              ]}
              data={dailyRows}
              initialSort={{ key: 'date', dir: 'asc' }}
              minWidth={620}
            />
          )}
        </div>
      )}

      {/* ── Tab: סופ״ש (BOM weekend breakdown) ── */}
      {hasPermission('movie_detail.charts_tab') && tab === 'weekend' && (
        <div style={{ animation: 'fadeIn .18s ease' }}>
          {!breakdown ? (
            <div style={{ textAlign: 'center', padding: 48, color: TEXT_TERTIARY }}>טוען...</div>
          ) : weekendRows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: TEXT_TERTIARY }}>אין נתוני סופ״ש</div>
          ) : (
            <RankingTable
              columns={[
                { key: 'label', header: 'סופ״ש', align: 'right', sortValue: r => r.start,
                  render: r => (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, color: TEXT_PRIMARY }}>{r.label}</span>
                      {r.includes_wednesday && <WednesdayChip />}
                    </span>
                  ) },
                { key: 'tickets', header: 'כרטיסים', align: 'center',
                  render: r => <span style={{ fontWeight: 700, color: ACCENT }}>{formatNumber(r.tickets)}</span> },
                { key: 'wow', header: 'מגמה', align: 'center', sortValue: r => WoWCell.sortValue(r.tickets, r.prevTickets),
                  render: r => <WoWCell curr={r.tickets} prev={r.prevTickets} /> },
                { key: 'screenings', header: 'הקרנות', align: 'center',
                  render: r => <span style={{ color: 'var(--muted)' }}>{r.screenings || '—'}</span> },
                { key: 'occupancy', header: 'תפוסה', align: 'center',
                  render: r => <div style={{ display: 'inline-flex' }}><OccBar value={r.occupancy} /></div> },
              ]}
              data={weekendRows}
              initialSort={{ key: 'label', dir: 'asc' }}
              minWidth={520}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default MovieDetail;
